import { useState, useCallback, useEffect } from "react";
import CashierShiftService from "../../../../services/cashier_shift_service";
import { toast } from "react-toastify";

export const useCashierShifts = (idTenant, user, selectedDate, canViewAllCashier) => {
    const [availableShifts, setAvailableShifts] = useState([]);
    const [currentShift, setCurrentShift] = useState(null);
    const [isProcessingShift, setIsProcessingShift] = useState(false);

    const loadShiftScope = useCallback(async () => {
        if (!idTenant || !user?.uid) return;

        try {
            if (canViewAllCashier) {
                const [myOpenShift, dailyShifts] = await Promise.all([
                    CashierShiftService.getMyOpenShift(idTenant, user.uid),
                    CashierShiftService.getShiftsByDate(idTenant, selectedDate)
                ]);

                setAvailableShifts(dailyShifts);
                setCurrentShift((prev) => {
                    if (prev?.id) {
                        const stillExists = dailyShifts.find(shift => shift.id === prev.id);
                        if (stillExists) return stillExists;
                    }
                    if (myOpenShift) return myOpenShift;
                    return dailyShifts[0] || null;
                });
                return;
            }

            const myShift = await CashierShiftService.getMyOpenShift(idTenant, user.uid);
            setAvailableShifts(myShift ? [myShift] : []);
            setCurrentShift(myShift);
        } catch (err) {
            console.error("[useCashierShifts] Erro carregando turno:", err);
        }
    }, [canViewAllCashier, idTenant, selectedDate, user]);

    useEffect(() => {
        loadShiftScope();
    }, [loadShiftScope]);

    const handleSelectShift = useCallback((shiftId) => {
        const nextShift = availableShifts.find(shift => shift.id === shiftId) || null;
        setCurrentShift(nextShift);
    }, [availableShifts]);

    const handleOpenShift = useCallback(async (openData, onSuccess) => {
        try {
            setIsProcessingShift(true);
            const shift = await CashierShiftService.openShift(idTenant, {
                idBankAccount: openData.idBankAccount,
                operatorId: user?.uid,
                operatorName: user?.displayName || user?.fullName || user?.name || user?.email || "",
                openingBalance: openData.openingBalance,
                actingUser: user,
                canManageAll: canViewAllCashier
            });
            setCurrentShift(shift);
            await loadShiftScope();
            toast.success("Caixa aberto com sucesso!");
            if (onSuccess) onSuccess();
        } catch (err) {
            console.error("Erro ao abrir caixa:", err);
            toast.error(err.message || "Não foi possível abrir o caixa.");
        } finally {
            setIsProcessingShift(false);
        }
    }, [canViewAllCashier, idTenant, loadShiftScope, user]);

    const handleCloseShift = useCallback(async (closeData, systemSummary, onSuccess) => {
        if (!currentShift?.id) return;
        try {
            setIsProcessingShift(true);
            const systemComputedBalance = currentShift.openingBalance + systemSummary.totalIncome - systemSummary.totalExpense;

            await CashierShiftService.closeShift(idTenant, currentShift.id, {
                closingBalance: closeData.closingBalance,
                systemComputedBalance,
                actingUser: user,
                canManageAll: canViewAllCashier
            });
            setCurrentShift(null);
            toast.success("Caixa fechado com sucesso!");
            await loadShiftScope();
            if (onSuccess) onSuccess();
        } catch (err) {
            console.error("Erro ao fechar caixa:", err);
            toast.error(err.message || "Não foi possível fechar o caixa.");
        } finally {
            setIsProcessingShift(false);
        }
    }, [canViewAllCashier, currentShift, idTenant, loadShiftScope, user]);

    return {
        availableShifts,
        currentShift,
        isProcessingShift,
        handleSelectShift,
        handleOpenShift,
        handleCloseShift
    };
};
